import FileDownloadIcon from '@mui/icons-material/FileDownload'
import FileUploadIcon from '@mui/icons-material/FileUpload'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import StraightenIcon from '@mui/icons-material/Straighten'
import { Accordion, AccordionSummary, accordionSummaryClasses, Grid, Stack, Typography, useMediaQuery } from '@mui/material'
import AccordionDetails from '@mui/material/AccordionDetails'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import theme from 'assets/theme'
import {
  KNDangerousGoodsIcon,
  KNQuoteDimensionIcon,
  KNQuotePalletIcon,
  KNVolumeIcon,
  KNWeightIcon,
} from 'components/KNIcon/KNMaterialIcon'
import KNQuoteAttribute from 'components/KNQuoteAttribute/KNQuoteAttribute'
import { GuestUserContext } from 'context/authentication/GuestUserContext'
import {
  getDimensionMeasurement,
  getDimensionUnit,
  getVolumeMeasurement,
  getVolumeUnit,
  getWeightMeasurement,
  getWeightUnit,
  getLoadingMetersMeasurement,
  getLoadingMetersUnit,
} from 'global/helpers/units'
import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import type { Cargo, Shipment } from 'screens/QuoteDetails/QuoteDetails.types'
import CarrierQuotesHeader from 'screens/QuoteDetails/CarrierQuotesHeader'
import DangerousGoodsDetails from 'screens/QuoteDetails/DangerousGoodsDetails'
import QuoteCargo from 'screens/QuoteDetails/QuoteCargo'
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIos'

export interface QuoteShipmentAccordionProps {
  shipment: Shipment
}

const QuoteShipmentAccordion = ({ shipment }: QuoteShipmentAccordionProps) => {
  const { t } = useTranslation()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [guestUserState, _guestUserDispatch] = useContext(GuestUserContext)
  const hasDangerousGoods = shipment.cargoItems.some(cargo => cargo.dangerousGoods.length > 0)

  const getNumberOfPackages = (cargo: Cargo): string => `${cargo.numberOfPackages} × ${cargo.packageType.toLowerCase()}`
  const getDimensions = (cargo: Cargo): string => `${getDimensionMeasurement(cargo.length!, guestUserState.measurementsSystem)} / ${getDimensionMeasurement(cargo.width!, guestUserState.measurementsSystem)} / ${getDimensionMeasurement(cargo.height!, guestUserState.measurementsSystem)}`

  return (
    <Accordion>
      <AccordionSummary
        expandIcon={
          <ArrowForwardIosSharpIcon sx={{ color: 'primary.main', fontSize: '1rem' }} />
        }
        sx={{
          flexDirection: 'row-reverse',
          alignItems: 'flex-start',
          backgroundColor: 'background.activeNavBackground',
          borderRadius: '12px',
          '& .MuiAccordionSummary-expandIconWrapper': {
            marginTop: 2,
          },
          [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]:
            {
              transform: 'rotate(90deg)',
            },
        }}
      >
        <Grid container sx={{ width: '100%' }}>
          <Grid size={{ xs: 12, sm: 7 }}>
            <Stack spacing={2} direction={isMobile ? 'column' : 'row'}>
              <CarrierQuotesHeader
                type="pickup"
                windowStartDate={shipment.pickup.windowStartDate}
                windowEndDate={shipment.pickup.windowEndDate}
                country={shipment.pickup.address.countryCode}
                city={shipment.pickup.address.city}
                zipCode={shipment.pickup.address.postalCode}
                icon={<FileUploadIcon />}
              />
              <CarrierQuotesHeader
                type="delivery"
                windowStartDate={shipment.delivery.windowStartDate}
                windowEndDate={shipment.delivery.windowEndDate}
                country={shipment.delivery.address.countryCode}
                city={shipment.delivery.address.city}
                zipCode={shipment.delivery.address.postalCode}
                icon={<FileDownloadIcon />}
              />
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, sm: 5 }}>
            <Stack direction="row" justifyContent="space-between">
              <QuoteCargo shipment={shipment}></QuoteCargo>
              {hasDangerousGoods && (
                <Chip label={t('screens.cs.carrier_quotes.quote_accordion.dangerous')} size="small" color="error" />
              )}
            </Stack>
          </Grid>
        </Grid>
      </AccordionSummary>

      <AccordionDetails sx={{ backgroundColor: 'background.activeNavBackground', marginTop: 1, paddingTop: 2, borderRadius: '12px' }}>
        {shipment.cargoItems.map((cargo, index) => {
          return (
            <Grid container spacing={2} key={`${cargo.description}-${cargo.numberOfPackages}`}>
              <Grid size={{ xs: 12, md: 5, lg: 4 }}>
                <Stack direction="column" pl={2}>
                  <Stack direction="row" mb={1} spacing={1}>
                    <KNQuotePalletIcon color="primary" />
                    <Typography variant="p4" color="primary.main">
                      {cargo.description}
                    </Typography>
                  </Stack>
                  {cargo.numberOfPackages !== null && <KNQuoteAttribute
                    value={getNumberOfPackages(cargo)}
                    prefix={t('screens.cs.carrier_quotes.quote_accordion.packages')}
                    icon={<Inventory2OutlinedIcon />}
                  />}

                  {cargo.grossWeight !== null && <KNQuoteAttribute
                    value={getWeightMeasurement(cargo.grossWeight, guestUserState.measurementsSystem)}
                    prefix={t('screens.cs.carrier_quotes.quote_accordion.weight')}
                    suffix={getWeightUnit(guestUserState.measurementsSystem)}
                    icon={<KNWeightIcon />}
                  />}

                  {cargo.volume !== null && <KNQuoteAttribute
                    value={getVolumeMeasurement(cargo.volume, guestUserState.measurementsSystem)}
                    prefix={t('screens.cs.carrier_quotes.quote_accordion.volume')}
                    suffix={getVolumeUnit(guestUserState.measurementsSystem)}
                    icon={<KNVolumeIcon />}
                  />}

                  {(cargo.length && cargo.width && cargo.height) && <KNQuoteAttribute
                    value={getDimensions(cargo)}
                    prefix={t('screens.cs.carrier_quotes.quote_accordion.dimension')}
                    suffix={getDimensionUnit(guestUserState.measurementsSystem)}
                    icon={<KNQuoteDimensionIcon />}
                  />}

                  {cargo.loadingMeters && <KNQuoteAttribute
                    value={getLoadingMetersMeasurement(cargo.loadingMeters, guestUserState.measurementsSystem)}
                    prefix={t('screens.cs.carrier_quotes.quote_accordion.loading_meters')}
                    suffix={getLoadingMetersUnit(guestUserState.measurementsSystem)}
                    icon={<StraightenIcon />}
                  />}

                  {cargo.stackable !== null && <KNQuoteAttribute
                    value={cargo.stackable ? t('general.yes') : t('general.no')}
                    prefix={t('screens.cs.carrier_quotes.quote_accordion.stackable')}
                    icon={<KNQuotePalletIcon />}
                  />}

                </Stack>
              </Grid>

              {cargo.dangerousGoods.length > 0 &&
                <Grid size={{ xs: 12, md: 7, lg: 8 }}>
                  <Stack direction="column">
                    <Stack direction="row" mb={1} spacing={1}>
                      <KNDangerousGoodsIcon color="primary" />
                      <Typography variant="p4" color="primary.main">
                        {t('screens.cs.carrier_quotes.quote_accordion.dangerous_goods_details')}
                      </Typography>
                    </Stack>
                    {cargo.dangerousGoods.map((dangerousGood) => (
                      <DangerousGoodsDetails key={dangerousGood.englishTechnicalName} dangerousGoodsDetails={dangerousGood} />
                    ))}
                  </Stack>
                </Grid>
              }
              {shipment.cargoItems.length !== index + 1 && (
                <Divider orientation="horizontal" variant="middle" flexItem sx={{ marginY: 2 }} />
              )}
            </Grid>
          )
        })}
      </AccordionDetails>
    </Accordion>
  )
}

export default QuoteShipmentAccordion

